import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const EVENT_TYPES = ["Corporativo", "Feira", "Shopping", "Casamento", "Aniversário", "Lançamento", "Convenção", "Outro"];

export default function QuoteDialog({ open, onClose, simulators = [], preselectSlug }) {
  const [form, setForm] = useState({
    name: "", company: "", phone: "", whatsapp: "", email: "",
    city: "", event_date: "", people_count: "", event_type: "",
    simulators: [], message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && preselectSlug) {
      setForm((f) => ({ ...f, simulators: [preselectSlug] }));
    }
    if (!open) {
      setTimeout(() => setForm({
        name: "", company: "", phone: "", whatsapp: "", email: "",
        city: "", event_date: "", people_count: "", event_type: "",
        simulators: [], message: "",
      }), 300);
    }
  }, [open, preselectSlug]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSim = (slug) =>
    setForm((f) => ({
      ...f,
      simulators: f.simulators.includes(slug)
        ? f.simulators.filter((s) => s !== slug)
        : [...f.simulators, slug],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("Preencha nome, e-mail, telefone e cidade.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      toast.success("Orçamento enviado! Retornaremos em breve.");
      // Also open WhatsApp with prefilled message
      openWhatsapp();
      onClose();
    } catch (err) {
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const openWhatsapp = () => {
    // load settings whatsapp number from window global set by home page
    const whats = window.__SIMEXP_WHATS__ || "5515999999999";
    const simNames = form.simulators.map((sl) => simulators.find((s) => s.slug === sl)?.name || sl).join(", ");
    const text = [
      `Olá! Sou ${form.name} e gostaria de um orçamento SIM EXPERIENCE.`,
      form.company && `Empresa: ${form.company}`,
      form.event_type && `Tipo: ${form.event_type}`,
      form.event_date && `Data: ${form.event_date}`,
      form.people_count && `Pessoas: ${form.people_count}`,
      form.city && `Cidade: ${form.city}`,
      simNames && `Simuladores: ${simNames}`,
      form.message && `Mensagem: ${form.message}`,
    ].filter(Boolean).join("\n");
    window.open(`https://wa.me/${whats.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[900] flex items-center justify-center p-4"
          data-testid="quote-dialog"
        >
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="relative w-full max-w-4xl rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 md:p-10 max-h-[92vh] overflow-y-auto"
          >
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full border border-white/10" data-testid="quote-close">
              <X className="w-4 h-4" />
            </button>
            <div className="mb-6">
              <div className="overline text-[#E31B23] mb-2">Solicitação de orçamento</div>
              <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">
                Vamos criar sua <span className="neon-red">experiência</span>.
              </h3>
              <p className="mt-2 text-neutral-400 text-sm">
                Preencha os campos abaixo. Retornaremos por e-mail e WhatsApp.
              </p>
            </div>

            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="quote-form">
              <Field label="Nome*" v={form.name} on={(v) => update("name", v)} tid="quote-name" />
              <Field label="Empresa" v={form.company} on={(v) => update("company", v)} tid="quote-company" />
              <Field label="Telefone*" v={form.phone} on={(v) => update("phone", v)} tid="quote-phone" />
              <Field label="WhatsApp" v={form.whatsapp} on={(v) => update("whatsapp", v)} tid="quote-whatsapp" />
              <Field label="E-mail*" type="email" v={form.email} on={(v) => update("email", v)} tid="quote-email" />
              <Field label="Cidade*" v={form.city} on={(v) => update("city", v)} tid="quote-city" />
              <Field label="Data do evento" type="date" v={form.event_date} on={(v) => update("event_date", v)} tid="quote-date" />
              <Field label="Quantidade de pessoas" v={form.people_count} on={(v) => update("people_count", v)} tid="quote-people" />
              <div>
                <label className="block overline text-neutral-400 mb-2">Tipo de evento</label>
                <select
                  value={form.event_type}
                  onChange={(e) => update("event_type", e.target.value)}
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#008CFF] outline-none"
                  data-testid="quote-eventtype"
                >
                  <option value="">Selecione</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block overline text-neutral-400 mb-2">Simuladores de interesse</label>
                <div className="flex flex-wrap gap-2">
                  {simulators.map((s) => {
                    const active = form.simulators.includes(s.slug);
                    return (
                      <button
                        type="button"
                        key={s.slug}
                        onClick={() => toggleSim(s.slug)}
                        className={`rounded-full px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                          active
                            ? "bg-[#E31B23] text-white border-[#E31B23]"
                            : "border-white/10 text-neutral-300 hover:border-white/30"
                        }`}
                        data-testid={`quote-sim-${s.slug}`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block overline text-neutral-400 mb-2">Mensagem</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#008CFF] outline-none resize-none"
                  placeholder="Conte um pouco sobre seu evento..."
                  data-testid="quote-message"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 justify-end mt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-white/15 px-6 py-3 text-sm uppercase tracking-widest text-white hover:border-white/40"
                  data-testid="quote-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E31B23] px-8 py-3 text-sm uppercase tracking-widest text-white font-semibold glow-red disabled:opacity-60"
                  data-testid="quote-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Enviar orçamento
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <label className="block overline text-neutral-400 mb-2">{label}</label>
      <input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        data-testid={tid}
        className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-[#008CFF] outline-none"
      />
    </div>
  );
}
